"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from "react";
import { DateTime } from "effect";
import { BellOff, Lock, Receipt, Check, Mail, ArrowRight, ExternalLink, ShieldCheck, RefreshCw, Loader2, CheckCircle2, Landmark } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useIdentity } from "@/lib/providers/identity";
import { useOrgLockoutStatus, useNotifyBillingAdmin, useBillingGateStatus, useOrgInvoices, useRecheckBillingAccess } from "@/entities/billing/model";
import type { InvoiceResponse } from "@/contexts/billing/collections/presentation/rpc/org-admin-billing-rpc";
import { formatBillingCurrency } from "./billing-helpers";
import { PAYMENT_PROCESSING_COPY, RESTORE_TIMING_COPY, isGatePaymentProcessing } from "../lib/payment-processing";
import { SuperAdminBillingGateExemptLockoutControl } from "./super-admin-billing-gate-exempt-control";
const BILLING_PATH = "/settings/billing";
const SUPPORT_EMAIL = "team@cova.ai";

/**
 * Full-page takeover shown when the org is in hard_lock phase. Replaces the
 * entire app shell. Calm, professional, action-first — the amount owed is the
 * hero and a single primary CTA routes to the (gate-exempt) billing page.
 *
 * Reason-aware: a deliberate super-admin collections hard-lock (§6.5), or any
 * lock the invoice-age track owns (`lockoutReason === "unpaid_invoice"`, e.g.
 * an invoice-billed send_invoice org), renders the unpaid-invoice variant
 * (amount + pay-now list + bank-vs-card); the card-dunning hard-lock renders
 * the payment-method variant. Before R8 only the forced flag chose the
 * invoice variant, so an invoice-track lock was told to "update payment".
 *
 * Audience: ANY org admin (Clerk admin role) is treated as able to pay — we
 * gate the pay actions on the org-admin role, NOT the narrow billing-admin
 * (`gateStatus.isAdmin` = currentUser===billingUserId), which hides the actions
 * from every admin who isn't the single billing contact.
 *
 * Rail-aware (spec billing/2026-10-01-009 §5.6): the copy never assumes a card.
 * A bank debit in flight (`payment_processing`) gets a calm "processing" note,
 * never failure wording (D7).
 */
export function HardLockPage() {
  const $ = _c(10);
  const {
    identity
  } = useIdentity();
  const {
    data: status
  } = useOrgLockoutStatus();
  const {
    data: gate
  } = useBillingGateStatus();
  const isOrgAdmin = identity?.isAdmin === true;
  let t0;
  if ($[0] !== gate) {
    t0 = isGatePaymentProcessing(gate);
    $[0] = gate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const paymentProcessing = t0;
  if (gate?.collectionsHardLockForced === true || gate?.lockoutReason === "unpaid_invoice") {
    let t1;
    if ($[2] !== gate || $[3] !== isOrgAdmin || $[4] !== paymentProcessing) {
      t1 = <CollectionsHardLockPage gate={gate} isOrgAdmin={isOrgAdmin} paymentProcessing={paymentProcessing} />;
      $[2] = gate;
      $[3] = isOrgAdmin;
      $[4] = paymentProcessing;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (!status) {
    return null;
  }
  let t1;
  if ($[6] !== isOrgAdmin || $[7] !== paymentProcessing || $[8] !== status) {
    t1 = <CardDunningHardLockPage status={status} isOrgAdmin={isOrgAdmin} paymentProcessing={paymentProcessing} />;
    $[6] = isOrgAdmin;
    $[7] = paymentProcessing;
    $[8] = status;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}

// ============================================================================
// Shared shell
// ============================================================================

function HardLockShell(t0) {
  const $ = _c(16);
  const {
    icon,
    heading,
    subhead,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex justify-center mb-9"><Image src="/covalogo.png" alt="Cova" width={116} height={38} priority={true} /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <div className="flex justify-center mb-6"><div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 ring-1 ring-amber-100">{icon}</div></div>;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== heading) {
    t3 = <h1 className="text-center text-[1.6rem] font-semibold leading-[1.15] tracking-tight text-[#091E42]">{heading}</h1>;
    $[3] = heading;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== subhead) {
    t4 = <p className="mx-auto mt-3 max-w-sm text-center text-sm leading-relaxed text-gray-600">{subhead}</p>;
    $[5] = subhead;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children) {
    t5 = <div className="mt-8 space-y-5">{children}</div>;
    $[7] = children;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mt-6"><RecheckAccessButton /></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mt-10 flex flex-col items-center gap-2.5"><div className="flex items-center gap-1.5 text-xs text-gray-400"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} /><span>Your data is safe — nothing has been lost.</span></div><a href={`mailto:${SUPPORT_EMAIL}`} className="text-xs text-gray-500 underline decoration-gray-300 underline-offset-2 transition-colors hover:text-[#091E42] hover:decoration-gray-400">Questions? {SUPPORT_EMAIL}</a><SuperAdminBillingGateExemptLockoutControl /></div>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t8 = <div className="min-h-screen w-full bg-white bg-[radial-gradient(60%_45%_at_50%_0%,rgba(86,81,255,0.05),transparent)] flex flex-col items-center justify-center px-4 py-12"><div className="w-full max-w-md">{t1}{t2}{t3}{t4}{t5}{t6}{t7}</div></div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/** The hero fact: how much is owed. */
function AmountHero(t0) {
  const $ = _c(10);
  const {
    label,
    amount,
    meta
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-[0.7rem] font-medium uppercase tracking-wide text-gray-400">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== amount) {
    t2 = <p className="mt-1.5 text-[2.5rem] font-semibold leading-none tracking-tight text-[#091E42]">{amount}</p>;
    $[2] = amount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== meta) {
    t3 = <p className="mt-2 text-xs text-gray-500">{meta}</p>;
    $[4] = meta;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="rounded-2xl border border-[#091E42]/[0.07] bg-white px-6 py-5 text-center shadow-[0_1px_2px_rgba(9,30,66,0.04),0_8px_24px_-12px_rgba(9,30,66,0.12)]">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** Solid brand primary CTA. Flat opaque page (not a glass surface) → a solid
 *  fill is the correct, unmissable primary action signifier. Hex literals
 *  because the `cova-brand` Tailwind token is undefined (emits no CSS). */
function PrimaryCTA(t0) {
  const $ = _c(8);
  const {
    href,
    children,
    external: t1
  } = t0;
  const external = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== external) {
    t2 = external ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : {};
    $[0] = external;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group flex h-11 w-full items-center justify-center gap-2 rounded-xl px-5", "bg-[#5651ff] text-sm font-semibold text-white", "shadow-[0_1px_2px_rgba(9,30,66,0.08),0_8px_20px_-8px_rgba(86,81,255,0.55)]", "transition-all hover:bg-[#4540cc] hover:shadow-[0_10px_24px_-8px_rgba(86,81,255,0.65)]", "active:scale-[0.98] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#5651ff]/40 focus-visible:ring-offset-2");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== href || $[6] !== t2) {
    t5 = <a href={href} {...t2} className={t3}>{children}{t4}</a>;
    $[4] = children;
    $[5] = href;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/** Calm amber note for bank-transfer clearing guidance. */
function BankTransferNote(t0) {
  const $ = _c(4);
  const {
    payByCardAvailable
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="font-semibold">Paid by bank transfer?</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline decoration-amber-300 underline-offset-2 hover:decoration-amber-500">{SUPPORT_EMAIL}</a>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = payByCardAvailable ? " Card payments on the invoice page clear instantly." : "";
  let t4;
  if ($[2] !== t3) {
    t4 = <div className="rounded-xl border border-amber-200/70 bg-amber-50/60 px-4 py-3 text-left"><p className="text-xs leading-relaxed text-amber-800">{t1} Transfers take a couple of business days to clear. Email{" "}{t2}{" "}once you've paid and we'll restore access right away.{t3}</p></div>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}

/** Calm brand-tinted note: a bank debit (PayTo) is in flight — never a failure. */
function PaymentProcessingNote() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-[#5651ff]" strokeWidth={2} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-start gap-3 rounded-xl border border-[#5651ff]/15 bg-[#5651ff]/5 px-4 py-3 text-left" data-testid="hard-lock-payment-processing">{t0}<p className="text-xs leading-relaxed text-[#091E42]"><span className="font-semibold">{PAYMENT_PROCESSING_COPY.chip}.</span>{" "}{PAYMENT_PROCESSING_COPY.hardLockNote}</p></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** "How fast does paying restore access": the card line plus its PayTo
 *  sibling where PayTo can exist at all (AU / AUD only — a hard rule). */
function RestoreTimingNote(t0) {
  const $ = _c(2);
  const {
    payToPossible
  } = t0;
  const t1 = payToPossible ? ` ${RESTORE_TIMING_COPY.payTo}` : "";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="text-center text-xs text-gray-500">{RESTORE_TIMING_COPY.card}{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function SectionLabel(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="text-[0.7rem] font-medium uppercase tracking-wide text-gray-400">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="h-px flex-1 bg-linear-to-r from-gray-200 to-transparent" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="flex items-center gap-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * "I've already paid — recheck access" — forces a live reconcile against Stripe
 * (self-heals a missed/delayed invoice.paid webhook). On success the lockout
 * queries invalidate; if the balance cleared the gate refetches and this page
 * unmounts. If a balance remains (e.g. a bank transfer still clearing) we say so.
 */
function RecheckAccessButton() {
  const $ = _c(15);
  const {
    mutate,
    isPending,
    data
  } = useRecheckBillingAccess();
  const stillLocked = data?.stillLocked === true;
  const cleared = data?.stillLocked === false;
  let t0;
  if ($[0] !== mutate) {
    t0 = () => mutate();
    $[0] = mutate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-medium transition-colors", "text-gray-600 hover:text-[#091E42]", "disabled:pointer-events-none disabled:opacity-60");
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== cleared || $[4] !== isPending) {
    t2 = isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Checking with Stripe…</> : cleared ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-600" />Payment confirmed — restoring access…</> : <><RefreshCw className="h-3.5 w-3.5" />I've already paid — recheck access</>;
    $[3] = cleared;
    $[4] = isPending;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isPending || $[7] !== t0 || $[8] !== t2) {
    t3 = <button type="button" onClick={t0} disabled={isPending} className={t1}>{t2}</button>;
    $[6] = isPending;
    $[7] = t0;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== stillLocked) {
    t4 = stillLocked && <p className="max-w-sm text-center text-xs leading-relaxed text-amber-700">We still see an outstanding balance. If you paid by bank transfer it can take a couple of business days to clear — email{" "}<a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline underline-offset-2">{SUPPORT_EMAIL}</a>{" "}and we'll restore access.</p>;
    $[10] = stillLocked;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <div className="flex flex-col items-center gap-2">{t3}{t4}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

// ============================================================================
// Collections variant — deliberate super-admin hard-lock for unpaid invoices
// ============================================================================

function CollectionsHardLockPage(t0) {
  const $ = _c(19);
  const {
    gate,
    isOrgAdmin,
    paymentProcessing
  } = t0;
  const {
    data: invoices
  } = useOrgInvoices(25, isOrgAdmin);
  let t1;
  if ($[0] !== gate.outstandingCurrency || $[1] !== gate.outstandingInvoiceCount || $[2] !== gate.outstandingTotalCents || $[3] !== gate.payByCardAvailable || $[4] !== invoices || $[5] !== isOrgAdmin || $[6] !== paymentProcessing) {
    const openInvoices = (invoices ?? []).filter(_temp);
    const currency = gate.outstandingCurrency ?? "aud";
    const totalCents = gate.outstandingTotalCents ?? 0;
    const count = gate.outstandingInvoiceCount;
    const invoiceWord = count === 1 ? "invoice" : "invoices";
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Receipt className="h-7 w-7 text-amber-500" strokeWidth={2} />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== currency || $[10] !== totalCents) {
      t3 = formatBillingCurrency(totalCents, currency);
      $[9] = currency;
      $[10] = totalCents;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== currency) {
      t4 = currency.toUpperCase();
      $[12] = currency;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    const t5 = `${count} ${invoiceWord} · ${t4}`;
    let t6;
    if ($[14] !== t3 || $[15] !== t5) {
      t6 = <AmountHero label="Outstanding balance" amount={t3} meta={t5} />;
      $[14] = t3;
      $[15] = t5;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== paymentProcessing) {
      t7 = paymentProcessing ? <PaymentProcessingNote /> : null;
      $[17] = paymentProcessing;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t1 = <HardLockShell icon={t2} heading={"Access paused \u2014 payment required"} subhead={isOrgAdmin ? `${count} unpaid ${invoiceWord} need to be settled to restore access for your team.` : `Your organisation has ${count} unpaid ${invoiceWord}. An administrator needs to settle the balance to restore access.`}>{t6}{t7}{isOrgAdmin ? <><div className="space-y-2"><PrimaryCTA href={BILLING_PATH}>{"View billing & pay"}</PrimaryCTA><RestoreTimingNote payToPossible={currency.toLowerCase() === "aud"} /></div>{openInvoices.length > 0 && <div className="space-y-2.5"><SectionLabel>Invoices</SectionLabel><div className="space-y-1.5">{openInvoices.map(_temp2)}</div></div>}<BankTransferNote payByCardAvailable={gate.payByCardAvailable} /></> : <div className="rounded-xl border border-[#091E42]/[0.07] bg-[#F7F8F9] px-4 py-3.5 text-center"><p className="text-xs leading-relaxed text-gray-600">Please ask an administrator in your organisation to settle the balance, or contact{" "}<a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-[#091E42] underline decoration-gray-300 underline-offset-2 hover:decoration-gray-500">{SUPPORT_EMAIL}</a>.</p></div>}</HardLockShell>;
    $[0] = gate.outstandingCurrency;
    $[1] = gate.outstandingInvoiceCount;
    $[2] = gate.outstandingTotalCents;
    $[3] = gate.payByCardAvailable;
    $[4] = invoices;
    $[5] = isOrgAdmin;
    $[6] = paymentProcessing;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function _temp2(inv_0) {
  return <InvoiceRow key={inv_0.id} invoice={inv_0} />;
}
function _temp(inv) {
  return inv.status === "open";
}
function InvoiceRow(t0) {
  const $ = _c(15);
  const {
    invoice
  } = t0;
  const t1 = invoice.number ?? "Invoice";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="truncate text-xs font-medium text-[#091E42]">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== invoice.amountDue || $[3] !== invoice.currency) {
    t3 = formatBillingCurrency(invoice.amountDue, invoice.currency);
    $[2] = invoice.amountDue;
    $[3] = invoice.currency;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <p className="text-xs text-gray-500">{t3}</p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div className="min-w-0">{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== invoice.hostedInvoiceUrl) {
    t6 = invoice.hostedInvoiceUrl && <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#5651ff]/20 bg-[#5651ff]/6 px-2.5 py-1.5 text-xs font-semibold text-[#5651ff] transition-colors hover:bg-[#5651ff]/10">Pay<ExternalLink className="h-3 w-3" strokeWidth={2.25} /></a>;
    $[10] = invoice.hostedInvoiceUrl;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-center justify-between gap-3 rounded-xl border border-[#091E42]/6 bg-white px-3.5 py-2.5 transition-shadow hover:shadow-[0_2px_8px_-2px_rgba(9,30,66,0.1)]">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

// ============================================================================
// Payment-method dunning variant — automatic collection failed after retries
// ============================================================================

type LockoutStatusData = NonNullable<ReturnType<typeof useOrgLockoutStatus>["data"]>;
function CardDunningHardLockPage(t0) {
  const $ = _c(22);
  const {
    status,
    isOrgAdmin,
    paymentProcessing
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Lock className="h-7 w-7 text-amber-500" strokeWidth={2} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DetailRow label="What happened" value="Payment could not be processed after multiple attempts" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== status.attemptCount) {
    t3 = status.attemptCount > 0 && <DetailRow label="Payment attempts" value={`${status.attemptCount}`} />;
    $[2] = status.attemptCount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== status.lastFailureDate) {
    t4 = status.lastFailureDate && <DetailRow label="Last attempt" value={formatDate(status.lastFailureDate)} />;
    $[4] = status.lastFailureDate;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== status.nextRetryDate) {
    t5 = status.nextRetryDate && <DetailRow label="Next automatic retry" value={formatDate(status.nextRetryDate)} />;
    $[6] = status.nextRetryDate;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div className="rounded-2xl border border-[#091E42]/[0.07] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(9,30,66,0.04)]"><div className="space-y-2.5">{t2}{t3}{t4}{t5}</div></div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== paymentProcessing) {
    t7 = paymentProcessing ? <PaymentProcessingNote /> : null;
    $[12] = paymentProcessing;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isOrgAdmin || $[15] !== status.billingAdminEmail || $[16] !== status.billingAdminName) {
    t8 = isOrgAdmin ? <div className="space-y-2"><PrimaryCTA href={BILLING_PATH}>Update payment method</PrimaryCTA><p className="text-center text-xs text-gray-500">Updating your payment method restores access within ~a minute.</p></div> : <MemberActions adminName={status.billingAdminName} adminEmail={status.billingAdminEmail} />;
    $[14] = isOrgAdmin;
    $[15] = status.billingAdminEmail;
    $[16] = status.billingAdminName;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <HardLockShell icon={t1} heading={"Access paused \u2014 update payment"} subhead="We couldn't process your payment after several attempts. To protect your account, access is paused until billing is resolved.">{t6}{t7}{t8}</HardLockShell>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function DetailRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="shrink-0 text-xs text-gray-500">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-right text-xs font-medium text-[#091E42]">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-start justify-between gap-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function MemberActions(t0) {
  const $ = _c(27);
  const {
    adminName,
    adminEmail
  } = t0;
  const {
    mutate: notifyAdmin,
    isPending,
    data
  } = useNotifyBillingAdmin();
  const [notifiedAt, setNotifiedAt] = useState(null);
  let t1;
  if ($[0] !== notifyAdmin) {
    t1 = () => {
      notifyAdmin();
      setNotifiedAt(new Date());
    };
    $[0] = notifyAdmin;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNotify = t1;
  const wasSent = data?.success === true;
  const wasRateLimited = data?.rateLimited === true;
  const wasSuppressed = data?.suppressed === true;
  const displayName = adminName ?? "your billing admin";
  if (wasSent && notifiedAt) {
    let t2;
    if ($[2] !== adminEmail || $[3] !== displayName || $[4] !== notifiedAt) {
      t2 = <NotificationSentState adminName={displayName} adminEmail={adminEmail} sentAt={notifiedAt} />;
      $[2] = adminEmail;
      $[3] = displayName;
      $[4] = notifiedAt;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (wasSuppressed) {
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <BellOff className="h-4 w-4 text-gray-500" />;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== displayName) {
      t3 = <div className="flex items-center gap-2 text-sm text-gray-600">{t2}<span>{displayName} has muted these notifications</span></div>;
      $[7] = displayName;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== adminEmail) {
      t4 = adminEmail ? <p className="text-xs text-gray-500">Reach them at{" "}<a href={`mailto:${adminEmail}`} className="underline underline-offset-2">{adminEmail}</a></p> : <p className="text-xs text-gray-500">Contact them another way to get this cleared.</p>;
      $[9] = adminEmail;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t3 || $[12] !== t4) {
      t5 = <div className="flex flex-col items-center gap-2">{t3}{t4}</div>;
      $[11] = t3;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (wasRateLimited) {
    let t2;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Check className="h-4 w-4 text-green-600" />;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    let t3;
    if ($[15] !== displayName) {
      t3 = <div className="flex items-center gap-2 text-sm text-gray-600">{t2}<span>{displayName} has already been notified</span></div>;
      $[15] = displayName;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== adminEmail) {
      t4 = adminEmail && <p className="text-xs text-gray-500">You can also reach them at{" "}<a href={`mailto:${adminEmail}`} className="underline underline-offset-2">{adminEmail}</a></p>;
      $[17] = adminEmail;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    let t5;
    if ($[19] !== t3 || $[20] !== t4) {
      t5 = <div className="flex flex-col items-center gap-2">{t3}{t4}</div>;
      $[19] = t3;
      $[20] = t4;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    return t5;
  }
  let t2;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex h-11 w-full items-center justify-center rounded-xl px-5", "bg-[#5651ff] text-sm font-semibold text-white", "shadow-[0_1px_2px_rgba(9,30,66,0.08),0_8px_20px_-8px_rgba(86,81,255,0.55)]", "transition-all hover:bg-[#4540cc] active:scale-[0.98]", "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#5651ff]/40 focus-visible:ring-offset-2", "disabled:opacity-60 disabled:pointer-events-none");
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const t3 = isPending ? "Sending\u2026" : `Notify ${displayName}`;
  let t4;
  if ($[23] !== handleNotify || $[24] !== isPending || $[25] !== t3) {
    t4 = <button type="button" onClick={handleNotify} disabled={isPending} className={t2}>{t3}</button>;
    $[23] = handleNotify;
    $[24] = isPending;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
}
function NotificationSentState(t0) {
  const $ = _c(23);
  const {
    adminName,
    adminEmail,
    sentAt
  } = t0;
  const {
    mutate: notifyAdmin,
    isPending
  } = useNotifyBillingAdmin();
  let t1;
  if ($[0] !== sentAt) {
    t1 = () => Math.max(1, Math.round((Date.now() - sentAt.getTime()) / 60000));
    $[0] = sentAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [minutesAgo] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100"><Check className="h-3.5 w-3.5 text-green-600" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== adminName) {
    t3 = <div className="flex items-center gap-2 text-sm text-[#091E42]">{t2}<span>{adminName} has been notified</span></div>;
    $[3] = adminName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = minutesAgo === 1 ? "1 minute" : `${minutesAgo} minutes`;
  let t5;
  if ($[5] !== t4) {
    t5 = <span>Sent {t4} ago</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="text-gray-300">|</span>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== notifyAdmin) {
    t7 = () => notifyAdmin();
    $[8] = notifyAdmin;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = isPending ? "Sending\u2026" : "Send again";
  let t9;
  if ($[10] !== isPending || $[11] !== t7 || $[12] !== t8) {
    t9 = <button type="button" onClick={t7} disabled={isPending} className="underline underline-offset-2 hover:text-[#091E42] disabled:opacity-50">{t8}</button>;
    $[10] = isPending;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t5 || $[15] !== t9) {
    t10 = <div className="flex items-center gap-3 text-xs text-gray-500">{t5}{t6}{t9}</div>;
    $[14] = t5;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== adminEmail) {
    t11 = adminEmail && <div className="flex items-center gap-2 text-xs text-gray-500"><Mail className="h-3.5 w-3.5" /><a href={`mailto:${adminEmail}`} className="underline underline-offset-2">{adminEmail}</a></div>;
    $[17] = adminEmail;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t3) {
    t12 = <div className="flex flex-col items-center gap-3">{t3}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t3;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function formatDate(date: DateTime.Utc): string {
  try {
    return new Intl.DateTimeFormat("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(DateTime.toDate(date));
  } catch {
    return "Unknown";
  }
}